import { FC, useContext, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { RadioGroup, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Button } from "capitalrx-components-2.0/lib/general";
import { Heading } from "capitalrx-components-2.0/lib/typography";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { ThemeSelectorContext } from "@/context";
import { getThemeButton, validatePhoneNumber, formatPhoneWithExtension } from "@/utils";

import {
  sameAsAuthorizedOfficialFaxNumberMessage,
  sameAsAuthorizedOfficialMessage,
  sameAsAuthorizedOfficialPhoneNumberMessage,
  sameAsPrimaryContactMessage,
  sameAsPrimaryContactPhoneNumberMessage,
  sameAsSecondaryContactMessage,
  sameAsSecondaryContactPhoneNumberMessage,
} from "./helper";

export interface BaseContact {
  readonly first_name: string;
  readonly last_name: string;
  readonly title: string;
  readonly phone_number: string;
  readonly email_address: string;
}

export interface ProviderContactInformationDataForm {
  // Authorized Official Details
  readonly official: BaseContact & {
    readonly fax_number: string;
  };

  // //Primary Contact
  readonly primary: BaseContact;

  // //Secondary Contact
  readonly secondary: BaseContact;

  // Notification Settings
  readonly email_notifications?: string;
}

export interface ContactInformationGroupFieldsProps {
  readonly readOnly?: boolean;
  readonly isSuperUser?: boolean;
  readonly onEdit?: () => void;
}

export const ContactInformationGroupFields: FC<ContactInformationGroupFieldsProps> = ({ isSuperUser, readOnly, onEdit }) => {
  const { sixFull } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const { getValues, trigger, control, setValue } = useFormContext<ProviderContactInformationDataForm>();
  const { primary_color } = useContext(ThemeSelectorContext);

  const [officialFirst, officialLast, primaryFirst, primaryLast, secondaryFirst, secondaryLast] = useWatch({
    control,
    name: [
      "official.first_name",
      "official.last_name",
      "primary.first_name",
      "primary.last_name",
      "secondary.first_name",
      "secondary.last_name",
    ],
  });

  // Watch phone/fax fields to normalize formatting while typing
  const [officialPhone, officialFax, primaryPhone, secondaryPhone, officialEmail, primaryEmail, secondaryEmail] = useWatch({
    control,
    name: [
      "official.phone_number",
      "official.fax_number",
      "primary.phone_number",
      "secondary.phone_number",
      "official.email_address",
      "primary.email_address",
      "secondary.email_address",
    ],
  });

  useEffect(() => {
    void trigger([
      "official.first_name",
      "official.last_name",
      "official.phone_number",
      "official.fax_number",
      "official.email_address",
      "primary.first_name",
      "primary.last_name",
      "primary.phone_number",
      "primary.email_address",
      "secondary.first_name",
      "secondary.last_name",
      "secondary.phone_number",
      "secondary.email_address",
    ]);
  }, [officialFirst, officialLast, officialPhone, officialFax, officialEmail, primaryFirst, primaryLast, primaryPhone, primaryEmail, secondaryFirst, secondaryLast, secondaryPhone, secondaryEmail, trigger]);

  /* Format phone/fax: insert dashes and 'x' for ext
     Only set when value changes to avoid update loops */
  useEffect(() => {
    const phoneFields = [
      { value: officialPhone, name: "official.phone_number" },
      { value: officialFax, name: "official.fax_number" },
      { value: primaryPhone, name: "primary.phone_number" },
      { value: secondaryPhone, name: "secondary.phone_number" },
    ];

    phoneFields.forEach(({ value, name }) => {
      if (value != undefined) {
        const formatted = formatPhoneWithExtension(value);
        if (formatted !== value) {
          (setValue as any)(name, formatted, { shouldValidate: false });
        }
      }
    });
  }, [officialPhone, officialFax, primaryPhone, secondaryPhone, setValue]);

  const getFullName = (prefix: "official" | "primary" | "secondary"): string | null => {
    const first = getValues(`${prefix}.first_name`)?.trim();
    const last = getValues(`${prefix}.last_name`)?.trim();
    return first && last ? `${first} ${last}` : null;
  };

  const hasSameValue = (value: string, otherValue?: string): boolean => {
    return !!value && !!otherValue && value.trim().toLocaleLowerCase() === otherValue.trim().toLocaleLowerCase();
  };

  const getDuplicateFullNameMessage = (group: string): string | undefined => {
    const official = getFullName("official");
    const primary = getFullName("primary");
    const secondary = getFullName("secondary");

    if (group === "official") {
      if (official && primary && official === primary) {
        return sameAsPrimaryContactMessage;
      }

      if (official && secondary && official === secondary) {
        return sameAsSecondaryContactMessage;
      }
    }

    if (group === "primary") {
      if (primary && official && primary === official) {
        return sameAsAuthorizedOfficialMessage;
      }

      if (primary && secondary && primary === secondary) {
        return sameAsSecondaryContactMessage;
      }
    }

    if (group === "secondary") {
      if (secondary && official && secondary === official) {
        return sameAsAuthorizedOfficialMessage;
      }

      if (secondary && primary && secondary === primary) {
        return sameAsPrimaryContactMessage;
      }
    }

    return undefined;
  };

  function validateContact(value: string, name: string): boolean | string {
    const [group, field] = name.split(".");

    if (["phone_number", "fax_number"].includes(field)) {
      const validationMessage = validatePhoneNumber(value);
      if (validationMessage !== true) {
        return validationMessage;
      }
    }

    if (["first_name", "last_name"].includes(field)) {
      const duplicateFullNameMessage = getDuplicateFullNameMessage(group);
      if (duplicateFullNameMessage) {
        return duplicateFullNameMessage;
      }
    }

    if (field === "phone_number") {
      if (group === "official") {
        if (hasSameValue(value, getValues("primary.phone_number"))) {
          return sameAsPrimaryContactMessage;
        }

        if (hasSameValue(value, getValues("secondary.phone_number"))) {
          return sameAsSecondaryContactMessage;
        }

        if (hasSameValue(value, getValues("official.fax_number"))) {
          return sameAsAuthorizedOfficialFaxNumberMessage;
        }
      }

      if (group === "primary") {
        if (hasSameValue(value, getValues("official.phone_number"))) {
          return sameAsAuthorizedOfficialMessage;
        }

        if (hasSameValue(value, getValues("secondary.phone_number"))) {
          return sameAsSecondaryContactMessage;
        }

        if (hasSameValue(value, getValues("official.fax_number"))) {
          return sameAsAuthorizedOfficialFaxNumberMessage;
        }
      }

      if (group === "secondary") {
        if (hasSameValue(value, getValues("official.phone_number"))) {
          return sameAsAuthorizedOfficialMessage;
        }

        if (hasSameValue(value, getValues("primary.phone_number"))) {
          return sameAsPrimaryContactMessage;
        }

        if (hasSameValue(value, getValues("official.fax_number"))) {
          return sameAsAuthorizedOfficialFaxNumberMessage;
        }
      }
    }

    if (name === "official.fax_number") {
      if (hasSameValue(value, getValues("official.phone_number"))) {
        return sameAsAuthorizedOfficialPhoneNumberMessage;
      }

      if (hasSameValue(value, getValues("primary.phone_number"))) {
        return sameAsPrimaryContactPhoneNumberMessage;
      }

      if (hasSameValue(value, getValues("secondary.phone_number"))) {
        return sameAsSecondaryContactPhoneNumberMessage;
      }
    }

    if (field === "email_address") {
      if (group === "official") {
        if (hasSameValue(value, getValues("primary.email_address"))) {
          return sameAsPrimaryContactMessage;
        }

        if (hasSameValue(value, getValues("secondary.email_address"))) {
          return sameAsSecondaryContactMessage;
        }
      }

      if (group === "primary") {
        if (hasSameValue(value, getValues("official.email_address"))) {
          return sameAsAuthorizedOfficialMessage;
        }

        if (hasSameValue(value, getValues("secondary.email_address"))) {
          return sameAsSecondaryContactMessage;
        }
      }

      if (group === "secondary") {
        if (hasSameValue(value, getValues("official.email_address"))) {
          return sameAsAuthorizedOfficialMessage;
        }

        if (hasSameValue(value, getValues("primary.email_address"))) {
          return sameAsPrimaryContactMessage;
        }
      }
    }

    return true;
  }

  return (
    <Stack spacing="large">
      <Stack>
        <Shelf>
          <Heading>Notification Settings</Heading>
          <Shelf.Spacer />
          {readOnly && onEdit && (
            <Button
              icon="edit"
              appearance={getThemeButton("secondary", primary_color)}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </Shelf>
        <RadioGroup.Field
          name="email_notifications"
          orientation="horizontal"
          hideLabel
          defaultValue="false"
          readOnly={readOnly}
        >
          <RadioGroup.Option value="false">Do not receive notifications</RadioGroup.Option>
          <RadioGroup.Option value="true">Receive email notifications</RadioGroup.Option>
        </RadioGroup.Field>
      </Stack>
      <Stack>
        <Heading>Authorized Official Details</Heading>
        <Grid>
          <Grid.Spread columns={sixFull}>
            <Textbox.Field
              label="First Name"
              name="official.first_name"
              testId="official_first_name"
              validator={(value) => validateContact(value, "official.first_name")}
              required
              readOnly={readOnly}
            />
            <Textbox.Field
              label="Last Name"
              name="official.last_name"
              testId="official_last_name"
              validator={(value) => validateContact(value, "official.last_name")}
              required
              readOnly={readOnly}
            />
            <Textbox.Field label="Job Title" name="official.title" testId="official_title" required readOnly={readOnly} />
            <Textbox.Field
              label="Phone Number"
              name="official.phone_number"
              testId="official_phone_number"
              instructions={!readOnly && "123-456-7890 x 123"}
              validator={(value) => validateContact(value, "official.phone_number")}
              required
              readOnly={readOnly}
            />
            <Textbox.Field
              label="Email Address"
              name="official.email_address"
              testId="official_email_address"
              validator={(value) => validateContact(value, "official.email_address")}
              readOnly
            />
            <Textbox.Field
              label="Fax Number"
              name="official.fax_number"
              testId="official_fax_number"
              instructions={!readOnly && "123-456-7890 x 123"}
              validator={(value) => validateContact(value, "official.fax_number")}
              readOnly={readOnly}
              required
            />
          </Grid.Spread>
        </Grid>
      </Stack>
      <Stack>
        <Heading>Primary Contact</Heading>
        <Grid>
          <Grid.Spread columns={sixFull}>
            <Textbox.Field
              label="First Name"
              name="primary.first_name"
              testId="primary_first_name"
              validator={(value) => validateContact(value, "primary.first_name")}
              required
              readOnly={readOnly}
            />
            <Textbox.Field
              label="Last Name"
              name="primary.last_name"
              testId="primary_last_name"
              validator={(value) => validateContact(value, "primary.last_name")}
              required
              readOnly={readOnly}
            />
            <Textbox.Field label="Job Title" name="primary.title" testId="primary_title" required readOnly={readOnly} />
            <Textbox.Field
              label="Phone Number"
              name="primary.phone_number"
              testId="primary_phone_number"
              instructions={!readOnly && "123-456-7890 x 123"}
              validator={(value) => validateContact(value, "primary.phone_number")}
              required
              readOnly={readOnly}
            />
            <Textbox.Field
              label="Email Address"
              name="primary.email_address"
              testId="primary_email_address"
              instructions={!readOnly && "email@address.com"}
              validator={(value) => validateContact(value, "primary.email_address")}
              required
              readOnly={readOnly || isSuperUser}
            />
          </Grid.Spread>
        </Grid>
      </Stack>
      <Stack>
        <Heading>Secondary Contact</Heading>
        <Grid>
          <Grid.Spread columns={sixFull}>
            <Textbox.Field
              label="First Name"
              name="secondary.first_name"
              testId="secondary_first_name"
              validator={(value) => validateContact(value, "secondary.first_name")}
              readOnly={readOnly}
            />
            <Textbox.Field
              label="Last Name"
              name="secondary.last_name"
              testId="secondary_last_name"
              validator={(value) => validateContact(value, "secondary.last_name")}
              readOnly={readOnly}
            />
            <Textbox.Field label="Job Title" name="secondary.title" testId="secondary_title" readOnly={readOnly} />
            <Textbox.Field
              label="Phone Number"
              name="secondary.phone_number"
              testId="secondary_phone_number"
              instructions={!readOnly && "123-456-7890 x 123"}
              validator={(value) => validateContact(value, "secondary.phone_number")}
              readOnly={readOnly}
            />
            <Textbox.Field
              label="Email Address"
              name="secondary.email_address"
              testId="secondary_email_address"
              instructions={!readOnly && "email@address.com"}
              validator={(value) => validateContact(value, "secondary.email_address")}
              readOnly={readOnly}
            />
          </Grid.Spread>
        </Grid>
      </Stack>
    </Stack>
  );
};
